// src/pages/student/WorkflowProgress.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Chip,
    Grid,
    Slide,
    Card,
    CardContent,
    LinearProgress,
    useTheme,
    InputAdornment,
    Tooltip,
    Alert,
    CircularProgress,
    Fade,
    Zoom,
    Divider,
    useMediaQuery
} from '@mui/material';
// Import Timeline components from @mui/lab
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import {
    Refresh as RefreshIcon,
    CheckCircle,
    Cancel,
    Warning,
    Schedule,
    Description,
    Lightbulb,
    Send,
    Edit,
    Upload,
    Timeline as TimelineIcon,
    TrendingUp,
    TrendingDown,
    Event,
    Assignment,
    Feedback,
    Verified,
    Pending,
    MenuBook,
    Article,
    HealthAndSafety,
    EventNote,
    HourglassEmpty
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIWorkflowMilestones,
    APIWorkflowTracking,
    APIWorkflowStats,
    APIWorkflowUpdateMilestone
} from '../../apiServices/APIs';
import { 
    getUserDetails
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
// NOTE: The component file is still named StudentBar.js
// but it now renders "Researcher Portal" as its visible title.
import StudentBar from '../../components/StudentBar';
import authService from '../../apiServices/authService';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const WorkflowProgress = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [milestones, setMilestones] = useState([]);
    const [workflow, setWorkflow] = useState(null);
    const [stats, setStats] = useState(null);
    const [selectedMilestone, setSelectedMilestone] = useState(null);
    const [openSubmitDialog, setOpenSubmitDialog] = useState(false);
    const [submissionNotes, setSubmissionNotes] = useState('');
    const [submissionFile, setSubmissionFile] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const isMounted = useRef(true);
    const studentId = getUserDetails("Id");

    // Handle logout
    const handleLogout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchAllData();
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        setError(null);

        try {
            await Promise.all([
                fetchMilestones(),
                fetchWorkflow(),
                fetchStats()
            ]);
        } catch (error) {
            setError("Failed to load workflow data");
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchMilestones = async () => {
        const result = await apiService.listById(APIWorkflowMilestones, studentId);
        if (result.success && isMounted.current) {
            setMilestones(Array.isArray(result.data) ? result.data : []);
        }
    };

    const fetchWorkflow = async () => {
        const result = await apiService.listById(APIWorkflowTracking, studentId);
        if (result.success && result.data && isMounted.current) {
            setWorkflow(result.data);
        }
    };

    const fetchStats = async () => {
        const result = await apiService.listById(APIWorkflowStats, studentId);
        if (result.success && result.data && isMounted.current) {
            setStats(result.data);
        }
    };

    const handleOpenSubmit = (milestone) => {
        setSelectedMilestone(milestone);
        setSubmissionNotes('');
        setSubmissionFile(null);
        setOpenSubmitDialog(true);
    };

    const handleCloseSubmit = () => {
        setOpenSubmitDialog(false);
        setSelectedMilestone(null);
        setSubmissionNotes('');
        setSubmissionFile(null);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && (file.type === 'application/pdf' || file.type === 'application/msword' || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
            setSubmissionFile(file);
        } else {
            showToast("Error", "Please upload a PDF or Word document", "error");
        }
    };

    const handleSubmitMilestone = async () => {
        if (!selectedMilestone) return;
        
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('updateId', selectedMilestone.id);
            formData.append('milestone_status', 'submitted');
            if (submissionNotes) {
                formData.append('submission_notes', submissionNotes);
            }
            if (submissionFile) {
                formData.append('submission_file', submissionFile);
            }
            
            const result = await apiService.update(APIWorkflowUpdateMilestone, formData);
            
            if (result && result.success) {
                showToast("Success", "Milestone submitted successfully", "success");
                fetchAllData();
                handleCloseSubmit();
            }
        } catch (error) {
            showToast("Error", "Failed to submit milestone", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const getStatusIcon = (status) => {
        switch(status) {
            case 'approved': return <CheckCircle sx={{ color: '#4caf50' }} />;
            case 'submitted': return <Send sx={{ color: '#ff9800' }} />;
            case 'under_review': return <Schedule sx={{ color: '#2196f3' }} />;
            case 'revision_requested': return <Feedback sx={{ color: '#f44336' }} />;
            case 'in_progress': return <Edit sx={{ color: '#2196f3' }} />;
            case 'not_started': return <HourglassEmpty sx={{ color: '#9e9e9e' }} />;
            default: return <Pending />;
        }
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'approved': return '#4caf50';
            case 'submitted': return '#ff9800';
            case 'under_review': return '#2196f3';
            case 'revision_requested': return '#f44336';
            case 'in_progress': return '#2196f3';
            case 'not_started': return '#9e9e9e';
            default: return '#9e9e9e';
        }
    };

    const getMilestoneIcon = (iconName) => {
        const icons = {
            'Lightbulb': <Lightbulb />,
            'Description': <Description />,
            'HealthAndSafety': <HealthAndSafety />,
            'Timeline': <TimelineIcon />,
            'MenuBook': <MenuBook />,
            'Article': <Article />,
            'Send': <Send />,
            'Event': <Event />,
            'CheckCircle': <CheckCircle />,
            'Edit': <Edit />,
            'Verified': <Verified />
        };
        return icons[iconName] || <Assignment />;
    };

    const getTimelineDotColor = (status) => {
        switch(status) {
            case 'approved': return 'success';
            case 'submitted': return 'warning';
            case 'under_review': return 'info';
            case 'revision_requested': return 'error';
            case 'in_progress': return 'info';
            default: return 'grey';
        }
    };

    if (loading) {
        return <LoadingState message="Loading Workflow Progress..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchAllData} title="Unable to Load Workflow" variant="research" fullPage retryText="Retry Loading" />;
    }

    return (
        <>
            <FullWidthContainer>
                <Box sx={{backgroundColor: 'white',borderRadius: 3 }}>
                <StudentBar notificationCount={36} />
                {/* Header Section */}
                <Paper elevation={2} sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        mb: { xs: 2, sm: 3 }, 
                        borderRadius: 3,
                        backgroundColor: 'white',
                    }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        flexWrap: 'wrap', 
                        gap: 2,
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        <Typography variant="h6" sx={{
                            fontWeight: 'bold',
                            color: theme.palette.primary.main,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}>
                            <TimelineIcon /> Research Workflow Progress
                        </Typography>

                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={fetchAllData}
                            sx={{ 
                                textTransform: 'none', 
                                borderRadius: '8px',
                                width: { xs: '100%', sm: 'auto' }
                            }}
                        >
                            Refresh
                        </Button>
                    </Box>

                    {/* Workflow Status Card */}
                    {workflow && (
                        <Card sx={{ 
                            mt: 3, 
                            bgcolor: theme.palette.primary.light + '10',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'translateY(-4px)' }
                        }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Grid container spacing={3} alignItems="center">
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h4" sx={{ 
                                                fontWeight: 'bold', 
                                                color: theme.palette.primary.main,
                                                fontSize: { xs: '1.5rem', sm: '2rem' }
                                            }}>
                                                {Math.round(workflow.overall_progress || 0)}%
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                Overall Progress
                                            </Typography>
                                            <LinearProgress 
                                                variant="determinate" 
                                                value={workflow.overall_progress || 0}
                                                sx={{ height: 8, borderRadius: 4, mt: 1 }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={2}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h5" sx={{ 
                                                fontWeight: 'bold', 
                                                color: 'success.main',
                                                fontSize: { xs: '1.25rem', sm: '1.5rem' }
                                            }}>
                                                {workflow.completed_milestones || 0}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                Completed
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={2}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h5" sx={{ 
                                                fontWeight: 'bold', 
                                                color: 'warning.main',
                                                fontSize: { xs: '1.25rem', sm: '1.5rem' }
                                            }}>
                                                {(workflow.total_milestones || 0) - (workflow.completed_milestones || 0)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                Remaining
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Chip
                                            icon={workflow.workflow_status === 'on_track' ? <TrendingUp /> : <TrendingDown />}
                                            label={`Status: ${(workflow.workflow_status || 'on_track').toUpperCase()}`}
                                            color={workflow.workflow_status === 'on_track' ? 'success' : workflow.workflow_status === 'at_risk' ? 'warning' : 'error'}
                                            sx={{ 
                                                width: '100%', 
                                                py: { xs: 1, sm: 2 }, 
                                                fontSize: { xs: '0.75rem', sm: '1rem' }
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    )}

                    {/* Stats Cards */}
                    {stats && (
                        <Grid container spacing={2} sx={{ mt: 2 }}>
                            <Grid item xs={6} sm={3}>
                                <Card sx={{ 
                                    textAlign: 'center', 
                                    bgcolor: theme.palette.info.light + '10',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-4px)' }
                                }}>
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <Typography variant="h4" sx={{ 
                                            fontWeight: 'bold',
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            {stats.total || 0}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                            Total Milestones
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Card sx={{ 
                                    textAlign: 'center', 
                                    bgcolor: theme.palette.success.light + '10',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-4px)' }
                                }}>
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <Typography variant="h4" sx={{ 
                                            fontWeight: 'bold',
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            {stats.completed || 0}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                            Completed
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Card sx={{ 
                                    textAlign: 'center', 
                                    bgcolor: theme.palette.warning.light + '10',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-4px)' }
                                }}>
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <Typography variant="h4" sx={{ 
                                            fontWeight: 'bold',
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            {stats.in_progress || 0}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                            In Progress
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Card sx={{ 
                                    textAlign: 'center', 
                                    bgcolor: (stats.overdue || 0) > 0 ? 'error.light' : 'success.light',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-4px)' }
                                }}>
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <Typography variant="h4" sx={{ 
                                            fontWeight: 'bold', 
                                            color: (stats.overdue || 0) > 0 ? 'error.main' : 'inherit',
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            {stats.overdue || 0}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                            Overdue
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}
                </Paper>
                </Box>
                {/* Milestones Timeline */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    <Paper elevation={2} sx={{ 
                        borderRadius: 3, 
                        overflow: 'auto',
                        backgroundColor: 'white',
                    }}>
                        <Box sx={{ p: { xs: 2, sm: 3 } }}>
                            <Typography variant="h6" sx={{ 
                                mb: 3, 
                                fontWeight: 'bold', 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                <EventNote /> Milestone Timeline
                            </Typography>

                            <Timeline position={isMobile ? "right" : "alternate"}>
                                {milestones.map((milestone, index) => (
                                    <TimelineItem key={milestone.id}>
                                        {!isMobile && (
                                            <TimelineOppositeContent color="text.secondary">
                                                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                                                    Due: {milestone.due_date ? new Date(milestone.due_date).toLocaleDateString() : 'N/A'}
                                                </Typography>
                                                {milestone.is_overdue && !milestone.completed_date && (
                                                    <Chip size="small" label="Overdue" color="error" sx={{ mt: 0.5 }} />
                                                )}
                                            </TimelineOppositeContent>
                                        )}
                                        <TimelineSeparator>
                                            <TimelineDot color={getTimelineDotColor(milestone.milestone_status)}>
                                                {getMilestoneIcon(milestone.icon)}
                                            </TimelineDot>
                                            {index < milestones.length - 1 && <TimelineConnector />}
                                        </TimelineSeparator>
                                        <TimelineContent>
                                            <Card sx={{ 
                                                p: { xs: 1.5, sm: 2 }, 
                                                bgcolor: milestone.milestone_status === 'approved' ? 'success.light' : 'background.paper',
                                                borderLeft: milestone.milestone_status === 'approved' ? `4px solid ${theme.palette.success.main}` : 'none'
                                            }}>
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'space-between', 
                                                    alignItems: 'flex-start', 
                                                    flexWrap: 'wrap', 
                                                    gap: 1,
                                                    flexDirection: { xs: 'column', sm: 'row' }
                                                }}>
                                                    <Box>
                                                        <Typography variant="subtitle1" sx={{ 
                                                            fontWeight: 'bold',
                                                            fontSize: { xs: '0.9rem', sm: '1rem' }
                                                        }}>
                                                            {milestone.type_display_name || milestone.milestone_name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                            {milestone.milestone_name}
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        icon={getStatusIcon(milestone.milestone_status)}
                                                        label={(milestone.milestone_status || 'not_started').replace('_', ' ').toUpperCase()}
                                                        sx={{
                                                            backgroundColor: getStatusColor(milestone.milestone_status) + '20',
                                                            color: getStatusColor(milestone.milestone_status),
                                                            fontWeight: 'bold',
                                                            fontSize: { xs: '0.65rem', sm: '0.75rem' }
                                                        }}
                                                        size="small"
                                                    />
                                                </Box>
                                                
                                                {isMobile && milestone.due_date && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: '0.7rem' }}>
                                                        Due: {new Date(milestone.due_date).toLocaleDateString()}
                                                        {milestone.is_overdue && !milestone.completed_date && (
                                                            <Chip size="small" label="Overdue" color="error" sx={{ ml: 1 }} />
                                                        )}
                                                    </Typography>
                                                )}

                                                {milestone.completed_date && (
                                                    <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                        Completed: {new Date(milestone.completed_date).toLocaleDateString()}
                                                    </Typography>
                                                )}

                                                {milestone.feedback && (
                                                    <Alert severity="info" sx={{ mt: 1, p: 1 }}>
                                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                            {milestone.feedback}
                                                        </Typography>
                                                    </Alert>
                                                )}

                                                {(milestone.milestone_status === 'not_started' || milestone.milestone_status === 'revision_requested') && (
                                                    <Button
                                                        size="small"
                                                        variant={milestone.milestone_status === 'revision_requested' ? "contained" : "outlined"}
                                                        color={milestone.milestone_status === 'revision_requested' ? "warning" : "primary"}
                                                        startIcon={milestone.milestone_status === 'revision_requested' ? <Upload /> : <Edit />}
                                                        onClick={() => handleOpenSubmit(milestone)}
                                                        sx={{ mt: 1, width: { xs: '100%', sm: 'auto' } }}
                                                    >
                                                        {milestone.milestone_status === 'revision_requested' ? 'Resubmit' : 'Start & Submit'}
                                                    </Button>
                                                )}
                                            </Card>
                                        </TimelineContent>
                                    </TimelineItem>
                                ))}
                            </Timeline>

                            {milestones.length === 0 && (
                                <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 } }}>
                                    <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        No milestones found
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </motion.div>

                {/* Submit Milestone Dialog */}
                <Dialog
                    open={openSubmitDialog}
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseSubmit();
                        }
                    }}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="sm"
                    disableEscapeKeyDown
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        textAlign: 'center',
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}>
                        <b>Submit Milestone</b>
                    </DialogTitle>
                    <DialogContent>
                        {selectedMilestone && (
                            <Box sx={{ p: { xs: 1, sm: 2 } }}>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                        <strong>Milestone:</strong> {selectedMilestone.type_display_name || selectedMilestone.milestone_name}<br/>
                                        <strong>Due Date:</strong> {selectedMilestone.due_date ? new Date(selectedMilestone.due_date).toLocaleDateString() : 'N/A'}
                                    </Typography>
                                </Alert>

                                <TextField
                                    fullWidth
                                    label="Submission Notes"
                                    multiline
                                    rows={4}
                                    value={submissionNotes}
                                    onChange={(e) => setSubmissionNotes(e.target.value)}
                                    placeholder="Provide any additional notes about your submission..."
                                    sx={{ mb: 2 }}
                                    size={isMobile ? "small" : "medium"}
                                />

                                <Button
                                    fullWidth
                                    variant="outlined"
                                    component="label"
                                    startIcon={<Upload />}
                                    sx={{ mb: 1 }}
                                    size={isMobile ? "small" : "medium"}
                                >
                                    Upload File (PDF/DOC/DOCX)
                                    <input
                                        type="file"
                                        hidden
                                        accept=".pdf,.doc,.docx"
                                        onChange={handleFileChange}
                                    />
                                </Button>

                                {submissionFile && (
                                    <Chip
                                        label={submissionFile.name}
                                        onDelete={() => setSubmissionFile(null)}
                                        color="primary"
                                        sx={{ mt: 1 }}
                                        size="small"
                                    />
                                )}

                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                    Maximum file size: 10MB. Accepted formats: PDF, DOC, DOCX
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button 
                            color="error" 
                            onClick={handleCloseSubmit} 
                            variant="outlined" 
                            disabled={actionLoading}
                            size={isMobile ? "small" : "medium"}
                        >
                            Cancel
                        </Button>
                        <Button 
                            color="primary" 
                            onClick={handleSubmitMilestone} 
                            variant="contained"
                            disabled={actionLoading}
                            startIcon={actionLoading ? <CircularProgress size={20} /> : <Send />}
                            size={isMobile ? "small" : "medium"}
                        >
                            Submit Milestone
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default WorkflowProgress;